import axios from 'axios'

import { hexToChipColor } from '@/utils/chipColor'

import useDate from './date'
import useLocalStorage from './useLocalStorage'
import useObject from './object'
import useRegex from './regex'

import type {
  CustomerCreation,
  CustomerCreationErrors,
  CustomerDetails,
  CustomerGender,
  CustomerTag
} from '@/types/types'

export default function useCustomer() {
  const router = useRouter()
  const { beforeToday } = useDate()
  const { hasNonNullValue } = useObject()
  const { isEmailValid } = useRegex()
  const { t } = useI18n()
  const { user } = useLocalStorage()
  const notification = useNotification()

  const merchantId = user.value.merchant.id
  const merchantAttributes = ref([])

  const { formatPhoneNumber } = useNumber()

  const getDisplayedCustomerName = (customer: CustomerDetails, maxLength = 20) => {
    const { firstName, lastName, phone } = customer
    let fullName = [firstName, lastName].filter(Boolean).join(' ')

    let wasSliced = false

    if (fullName.length > maxLength) {
      fullName = fullName.slice(0, maxLength)

      fullName = fullName.slice(0, Math.min(fullName.length, fullName.lastIndexOf(' ')))
      wasSliced = true
    }

    if (wasSliced && fullName.length < 17) {
      fullName += '...'
    }

    if (firstName === null && lastName.length > maxLength) {
      fullName = `${lastName.slice(0, maxLength).slice(0, Math.min(lastName.length, lastName.lastIndexOf(' ')))}...`
    }

    return fullName || firstName || lastName || formatPhoneNumber(phone)
  }

  const getTagBy = (attr: string, attrValue: string | boolean): CustomerTag[] => {
    const { customerTags } = useLocalStorage()
    return customerTags.value
      .filter((tag) => tag?.[attr] === attrValue)
      .map((tag) => ({ ...tag, color: hexToChipColor(tag.color) }))
  }

  const getImagePathFromGenderAndPhone = (gender: string, phone: string) => {
    switch (gender) {
      case 'woman':
        return `/images/avatars/woman${randomNumberFromPhone(phone)}.jpg`
      case 'man':
        return `/images/avatars/man${randomNumberFromPhone(phone)}.jpg`
      default:
        return '/images/avatars/default.jpg'
    }
  }
  const randomNumberFromPhone = (phone: string) => {
    return ((Number(phone) % 10) + 1).toString().padStart(2, '0')
  }

  const gendersList = <CustomerGender[]>[
    {
      id: 1,
      slug: null,
      text: t('pages.customer.customerPage.blocks.create.fields.genders.unknown')
    },
    {
      id: 2,
      slug: 'man',
      text: t('pages.customer.customerPage.blocks.create.fields.genders.man')
    },
    {
      id: 3,
      slug: 'woman',
      text: t('pages.customer.customerPage.blocks.create.fields.genders.woman')
    },
    {
      id: 4,
      slug: 'undisclosed',
      text: t('pages.customer.customerPage.blocks.create.fields.genders.undisclosed')
    },
    {
      id: 5,
      slug: 'other',
      text: t('pages.customer.customerPage.blocks.create.fields.genders.other')
    }
  ]

  const customerErrors = ref<CustomerCreationErrors>({
    phone: null,
    referenceStore: null,
    email: null,
    points: null,
    birthday: null,
    postalCode: null,
    city: null
  })

  const checkCustomerFormErrors = (newCustomer: CustomerCreation) => {
    const nonNullFields = ['phone', 'referenceStore']

    for (const field of nonNullFields) {
      if (newCustomer[field] === null || newCustomer[field] === '' || newCustomer[field] < 0) {
        customerErrors.value[field] = t(`pages.customer.customerPage.notifications.create.${field}`)
      } else {
        customerErrors.value[field] = null
      }
    }

    if (!!newCustomer.email && !isEmailValid(newCustomer.email)) {
      customerErrors.value.email = t('pages.customer.customerPage.notifications.create.email', {
        email: '(john@domain.com)'
      })
    } else {
      customerErrors.value.email = null
    }
    if (!!newCustomer.birthdayFormatted && !beforeToday(newCustomer.birthdayFormatted)) {
      customerErrors.value.birthday = t('pages.customer.customerPage.notifications.create.birthday')
    } else {
      customerErrors.value.birthday = null
    }

    return Object.values(customerErrors.value).some((field) => field !== null)
  }

  const setFormErrors = (error: {
    message: string
    errors: {
      [key: string]: string[]
    }
  }) => {
    const keyMap = {
      phone_number: 'phone'
    }
    if (error.errors) {
      Object.keys(error.errors).forEach((key) => {
        const errorKey = keyMap[key] ?? key
        if (errorKey in customerErrors.value) {
          if (errorKey === 'birthday') {
            customerErrors.value[errorKey] = t('pages.customer.customerPage.notifications.create.birthday')
          } else {
            customerErrors.value[errorKey] = error.errors[key][0] || error.message
          }
        }
      })
    }
  }

  const resetCustomerErrors = () => {
    customerErrors.value = {
      phone: null,
      referenceStore: null,
      email: null,
      points: null,
      birthday: null,
      postalCode: null,
      city: null
    }
  }
  const getMerchantAttributes = async () => {
    try {
      const { data } = await axios.get('customers/attributes')
      merchantAttributes.value = data.data
      return merchantAttributes.value
    } catch (error) {
      return error
    }
  }

  const updateCustomSegments = async (customer) => {
    const customItems = customer.value.lists?.filter((item) => item.isCustom)
    const updatePromises = customItems.map(async (customItem) => {
      try {
        const response = await axios.post(`/merchants/${merchantId}/segments`, {
          type: 'static',
          name: customItem.name
        })
        customItem.id = response.data.data.id
        return response
      } catch (error) {
        notification.create('error', {
          text: error
        })
        return Promise.reject(error)
      }
    })
    await Promise.all(updatePromises)
  }

  const updateCustomerLists = async (customerId: number, segmentIds) => {
    const uniqueSegmentIds = [...new Set(segmentIds)]
    try {
      await axios.put(`customers/${customerId}/segments`, { segmentIds: uniqueSegmentIds })
    } catch {
      notification.create('error', {
        text: t('notifications.save.error'),
        timeout: 5000
      })
      throw new Error('Rate limit error')
    }
  }

  const saveCustomerTags = async (customerId: number, newCustomer) => {
    try {
      await axios.post(`customers/${customerId}/tags`, {
        tagIds: newCustomer.tags
      })
      newCustomer.tags = []
    } catch {
      notification.create('error', {
        text: t('notifications.save.error'),
        timeout: 5000
      })
      throw new Error('Rate limit error')
    }
  }

  const saveCustomerAttributes = async (customerId: number, selectedAttributes) => {
    const requests = Object.entries(selectedAttributes)

      .filter(([_key, value]) => value !== null)
      .map(([key, value]) =>
        axios.post(`customers/${customerId}/attributes`, {
          attributeId: key,
          value: value
        })
      )

    await Promise.all(requests)
  }

  const isCustomerLoading = ref(false)

  const addCustomer = async (newCustomer, selectedAttributes) => {
    if (isCustomerLoading.value) {
      return
    }

    isCustomerLoading.value = true

    if (!checkCustomerFormErrors(newCustomer)) {
      const requestParams = {
        birthday: newCustomer.birthdayFormatted,
        city: newCustomer.city,
        country: newCustomer.country,
        email: newCustomer.email,
        firstName: newCustomer.firstname,
        gender: newCustomer.gender ? gendersList.find((gender) => gender.id === newCustomer.gender).slug : null,
        hasCheckin: false,
        hasOptin: newCustomer.optin,
        hasOptinEmail: newCustomer.hasOptinEmail,
        lastName: newCustomer.lastname,
        phone: newCustomer.phone?.replace(/\s/g, ''),
        points: newCustomer.points,
        postalCode: newCustomer.postalCode,
        referenceStoreId: newCustomer.referenceStore.id,
        segments: newCustomer.lists,
        source: 'added_manually_from_dashboard'
      }

      try {
        const addCustomerRes = await axios.post('customers/withPoints', requestParams)
        notification.create('success', {
          text: t('pages.customer.customerPage.notifications.create.success')
        })
        newCustomer.phone = null
        newCustomer.points = 0
        newCustomer.referenceStore = null
        newCustomer.country = null

        if (hasNonNullValue(selectedAttributes)) {
          await saveCustomerAttributes(addCustomerRes.data.data.id, selectedAttributes)
        }

        if (newCustomer.lists.length > 0) {
          await updateCustomerLists(
            addCustomerRes.data.data.id,
            newCustomer.lists.map((list) => list.id)
          )
        }

        if (newCustomer.tags.length > 0) {
          await saveCustomerTags(addCustomerRes.data.data.id, newCustomer)
        }

        return true
      } catch (e) {
        if (e.response.data) setFormErrors(e.response.data)

        if (e.response.data?.message) {
          if (e.response.status === 409) {
            router.push({ name: 'CustomerDetail', params: { customerId: e.response.data.customerId } })
          }
          if (e.response.status === 422) {
            const isBirthdayError = e.response.data.errors?.birthday
            notification.create('error', {
              text: isBirthdayError
                ? t('pages.customer.customerPage.notifications.create.error')
                : e.response.data.message
            })
          }
        }
      } finally {
        isCustomerLoading.value = false
      }
    }
  }

  return {
    addCustomer,
    checkCustomerFormErrors,
    customerErrors,
    gendersList,
    getDisplayedCustomerName,
    getImagePathFromGenderAndPhone,
    getMerchantAttributes,
    getTagBy,
    isCustomerLoading,
    merchantAttributes,
    resetCustomerErrors,
    setFormErrors,
    updateCustomerLists,
    updateCustomSegments
  }
}
